import { useEffect, useState } from "react";
import { ABFA_CREST, screenClock, isLive, goalSide, minuteLabel, GOAL_TYPES } from "@/lib/bigScreen";

// Fixed 1920x1080 canvas scaled to any 16:9 (or other) display — identical layout on HD and 4K.
export const Stage = ({ children }) => {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  return (
    <div className="fixed inset-0 bg-[#030a17] overflow-hidden flex items-center justify-center" data-testid="big-screen-stage">
      <div className="relative shrink-0 bs-canvas" style={{ width: 1920, height: 1080, transform: `scale(${scale})` }}>
        <div className="absolute inset-0 bs-bg" />
        {/* 5% overscan-safe area */}
        <div className="absolute inset-[54px_96px] flex flex-col">{children}</div>
      </div>
    </div>
  );
};

export const Crest = ({ team, size = 220 }) => (
  <div className="flex items-center justify-center rounded-full bg-white/[0.04] ring-1 ring-white/10" style={{ width: size, height: size }}>
    {team?.logo_url
      ? <img src={team.logo_url} alt={team?.name} className="object-contain" style={{ width: size * 0.78, height: size * 0.78 }} />
      : <span className="font-heading font-black text-white/30" style={{ fontSize: size * 0.3 }}>{(team?.short_name || team?.name || "?").slice(0, 3).toUpperCase()}</span>}
  </div>
);

export const TopBar = ({ centre, right }) => {
  const comp = centre?.competition;
  const m = centre?.match;
  return (
    <div className="flex items-center gap-6 h-[72px]" data-testid="bs-topbar">
      <img src={ABFA_CREST} alt="ABFA" className="h-[72px] w-[72px] object-contain" />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold uppercase tracking-[0.18em] text-[30px] text-white leading-none truncate" data-testid="bs-competition">
          {comp?.name || "ABFA Football Centre"}
        </p>
        <p className="text-[22px] text-white/55 mt-1 truncate" data-testid="bs-venue">
          {[comp?.season, m?.matchday ? `Matchday ${m.matchday}` : m?.round, m?.stadium].filter(Boolean).join("  •  ")}
        </p>
      </div>
      {right}
    </div>
  );
};

export const LiveBadge = ({ live }) => live ? (
  <span className="flex items-center gap-3 px-6 py-2 bg-[#E10600] text-white font-heading font-black text-[30px] tracking-[0.2em] uppercase" data-testid="bs-live-badge">
    <span className="w-3.5 h-3.5 rounded-full bg-white animate-pulse" /> Live
  </span>
) : null;

const Scorers = ({ events, match, side }) => {
  const goals = events.filter((e) => GOAL_TYPES.includes(e.type) && goalSide(e, match) === side);
  return (
    <div className={`text-[26px] text-white/70 leading-snug ${side === "home" ? "text-right" : "text-left"}`} data-testid={`bs-scorers-${side}`}>
      {goals.slice(-4).map((g) => (
        <p key={g.event_id} className="truncate">
          {g.player_name || "Goal"} {minuteLabel(g)}{g.type === "penalty_goal" ? " (P)" : g.type === "own_goal" ? " (OG)" : ""}
        </p>
      ))}
    </div>
  );
};

export const Scoreboard = ({ centre, skewMs, tick, stale }) => {
  const { match, home_team: home, away_team: away, events = [], clock, shootout } = centre;
  const c = screenClock(clock, match, skewMs.current);
  const live = isLive(match, clock) && !stale; // never show LIVE on stale data
  const showScore = match.home_score != null && !["pre_match"].includes(clock?.clock_state) || match.status === "completed";
  const hasShootout = shootout?.home_score != null || shootout?.kicks?.length > 0;
  return (
    <div className="flex-1 flex flex-col bs-fade" data-testid="bs-scoreboard" data-tick={tick}>
      <TopBar centre={centre} right={<LiveBadge live={live} />} />
      <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-10 mt-6">
        <div className="flex flex-col items-center gap-6">
          <Crest team={home} size={260} />
          <p className="font-heading font-black uppercase text-white text-[64px] leading-none text-center tracking-tight" data-testid="bs-home-name">{home?.name}</p>
        </div>
        <div className="flex flex-col items-center">
          {showScore ? (
            <div className="flex items-center gap-10 font-heading font-black text-white leading-none tabular-nums" data-testid="bs-score">
              <span className="text-[300px] bs-digit" data-testid="bs-home-score">{match.home_score ?? 0}</span>
              <span className="text-[140px] text-white/25">–</span>
              <span className="text-[300px] bs-digit" data-testid="bs-away-score">{match.away_score ?? 0}</span>
            </div>
          ) : (
            <p className="font-heading font-black text-[180px] text-white/90 leading-none" data-testid="bs-vs">VS</p>
          )}
          {hasShootout && (
            <p className="font-heading font-bold text-[44px] text-[#FFC72C] tracking-widest mt-2" data-testid="bs-shootout">
              PENS {shootout.home_score ?? 0} – {shootout.away_score ?? 0}
            </p>
          )}
        </div>
        <div className="flex flex-col items-center gap-6">
          <Crest team={away} size={260} />
          <p className="font-heading font-black uppercase text-white text-[64px] leading-none text-center tracking-tight" data-testid="bs-away-name">{away?.name}</p>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-10">
        <Scorers events={events} match={match} side="home" />
        <div className="flex flex-col items-center min-w-[560px]" data-testid="bs-clock">
          <div className="flex items-baseline gap-4 px-12 py-3 bg-white/[0.06] ring-1 ring-white/10">
            <span className={`font-heading font-black tabular-nums text-white leading-none ${c.special ? "text-[88px]" : "text-[120px]"}`} data-testid="bs-clock-main">{c.main}</span>
            {c.added && <span className="font-heading font-black text-[64px] text-[#FFC72C] tabular-nums" data-testid="bs-clock-added">{c.added}</span>}
          </div>
          <p className="font-heading font-bold uppercase tracking-[0.3em] text-[34px] text-[#4FD1E8] mt-4" data-testid="bs-period">
            {clock?.clock_state === "pre_match" && match.match_time ? `Kick-off ${match.match_time}` : c.period}
          </p>
        </div>
        <Scorers events={events} match={match} side="away" />
      </div>
    </div>
  );
};

export const IdleScreen = ({ message = "ABFA Football Centre" }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center bs-fade" data-testid="bs-idle">
    <img src={ABFA_CREST} alt="ABFA" className="w-[360px] h-[360px] object-contain mb-10" />
    <p className="font-heading font-black uppercase text-white text-[96px] leading-none tracking-tight">{message}</p>
    <p className="text-[32px] text-white/50 mt-6 tracking-[0.3em] uppercase">Antigua &amp; Barbuda Football Association</p>
  </div>
);
